import { AxiosInstance, AxiosResponse } from 'axios'
import { inject } from 'tsyringe'
import { httpToken } from '~/constants/dependency-injection/tokens'
import { containerScoped } from '~/decorators/dependency-container'
import { SchemaResult } from '~/models/report/message/schema'
import { SearchFieldSet, MessageReport } from '~/models/report/message/types'
import { ActionResult, Result } from '~/models/shared/result'
import { Page } from '~/models/common/types'
import { invalidBodyError } from '../../errors'
import { formatPage, formatIdent, formatSchemaField } from '../../formatters'
import RequestBuilder from '~/builders/http/RequestBuilder'

@containerScoped()
export default class MessageReportService {
  constructor(
    @inject(httpToken) private http: AxiosInstance,
    @inject(RequestBuilder) private requestBuilder: RequestBuilder
  ) {}

  async getReportModalSchema(messageId: number): Promise<SchemaResult> {
    const response: AxiosResponse = await this.http.get(
      `/api/account/messages/${messageId}/report/`
    )
    const { data: body } = response
    if (!body) {
      throw invalidBodyError(body)
    }
    return this.formatSchemaResponse(body.data)
  }

  async reportMessage(
    messageId: number,
    { reason, details }: { reason: number | null; details: string | null } = {
      reason: null,
      details: null
    }
  ): Promise<ActionResult> {
    const response: AxiosResponse = await this.http.post(
      `/api/account/messages/${messageId}/report/`,
      {
        reason,
        details: details || undefined
      }
    )
    const { data: body } = response
    if (!body) {
      throw invalidBodyError(body)
    }
    return body
  }

  private formatSchemaResponse(r: any): SchemaResult {
    const { details, reason } = r.schema

    if (r.reasons) {
      // move the values to the schema
      // TODO: Maybe ask the back end guys to decide on a format (that would be nice)
      r.schema.reason.values = r.reasons
    }

    return {
      schema: {
        details: details && formatSchemaField(details),
        reason: reason && formatSchemaField(reason)
      }
    }
  }

  async getMessageReports(
    page: Page,
    { messageQuery, reporterQuery, username, viewed }: SearchFieldSet
  ): Promise<Result<MessageReport[]>> {
    const response = await this.http.get('/api/admin/reports/messages/', {
      params: {
        [page.paramName || 'pg']: page.number,
        q: messageQuery,
        q2: reporterQuery,
        username,
        viewed: typeof viewed === 'boolean' ? (viewed ? 't' : 'f') : null
      }
    })
    const body = response.data
    if (
      !body ||
      !body.data ||
      !body.data.reports ||
      !body.data.reports.rows ||
      !body.data.reports.pagination
    ) {
      throw invalidBodyError(body)
    }

    const pagination = body.data.reports.pagination
    return {
      data: body.data.reports.rows.map(this.formatReport),
      page: formatPage(pagination)
    }
  }

  async toggleMessageReportViewedState(id: string): Promise<boolean> {
    const { data: body } = await this.http.delete(
      `/api/admin/reports/messages/${id}/`
    )

    if (!body.data || typeof body.data.viewed !== 'boolean') {
      throw invalidBodyError(body)
    }

    return body.data.viewed
  }

  // TODO: move this to MessagesService when it's created
  deleteMessage(threadId: string, messageId: string) {
    return this.requestBuilder
      .request('delete', `/api/account/messages/${threadId}/${messageId}/`)
      .send()
  }

  private formatReport(r: any): MessageReport {
    const rm = r.message
    const reason = r.reason
    return {
      id: r.id,
      site: r.site,
      reporter: formatIdent(r.reporter),
      reportedUser: formatIdent(r.reported_user),
      details: r.details,
      creationDate: r.created,
      message: {
        deleted: rm && rm.deleted,
        id: rm && rm.id && rm.id.toString(),
        text: rm && rm.text,
        ip: rm && rm.ip,
        threadId: rm && rm.thread_id && rm.thread_id.toString(),
        clsfdId: rm && rm.classified_id && rm.classified_id.toString()
      },
      reason: {
        name: reason && reason.name,
        value: reason && reason.value && reason.value.toString()
      },
      viewed: r.viewed
    }
  }
}
